/**
 * @file Popup Blocking Detector
 * @module utils/popupDetector
 * @desc Detects if popups are blocked in the current browser
 * Uses a non-invasive test popup that's immediately closed
 */

/**
 * Detects if popups are blocked in the current browser.
 *
 * This function attempts to open a tiny test popup and immediately
 * closes it. If the popup is blocked, returns true. Browsers usually
 * indicate blocking via a null return value or undefined check.
 *
 * @returns {Promise<boolean>} Resolves to true if popups are blocked, false otherwise
 *
 * @example
 * const isBlocked = await detectPopupBlocking()
 * if (isBlocked) {
 *   showWarningBanner('Popups are blocked for this site')
 * }
 */
export async function detectPopupBlocking() {
  return new Promise((resolve) => {
    try {
      const testWindow = window.open('', '', 'width=1,height=1')

      if (testWindow === null || typeof testWindow === 'undefined') {
        // Popup was blocked by the browser
        resolve(true)
      } else {
        // Popup was allowed - close it immediately
        testWindow.close()
        resolve(false)
      }
    } catch (error) {
      // If an error occurs, assume popups might be blocked
      resolve(true)
    }
  })
}

/**
 * Returns browser-specific instructions for enabling popups.
 *
 * @returns {Object} Object with browser name and instructions
 *
 * @example
 * const instructions = getPopupInstructions()
 * // {
 * //   browser: 'Safari',
 * //   steps: ['1. Open Safari Preferences...', ...]
 * // }
 */
export function getPopupInstructions() {
  const userAgent = navigator.userAgent.toLowerCase()

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return {
      browser: 'Mobile Safari',
      steps: [
        'Open Settings > Safari',
        'Scroll down and tap "Pop-ups"',
        'Toggle the switch to "Allow"',
      ],
    }
  }

  if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
    return {
      browser: 'Safari',
      steps: [
        'Click Safari in the menu bar',
        'Select "Preferences" (or press ⌘,)',
        'Click the "Security" tab',
        'Check "Allow pop-ups" option',
        'Alternatively, click the pop-up icon next to the address bar when blocked',
      ],
    }
  }

  if (/chrome/.test(userAgent)) {
    return {
      browser: 'Chrome',
      steps: [
        'Click the lock icon next to the URL',
        'Next to "Pop-ups", select "Allow"',
        'Reload the page',
        'Alternatively: Settings > Privacy and security > Site Settings > Pop-ups and redirects',
      ],
    }
  }

  if (/firefox/.test(userAgent)) {
    return {
      browser: 'Firefox',
      steps: [
        'Click the shield icon next to the address bar',
        'Toggle off "Enhanced Tracking Protection" (if needed)',
        'Or: Preferences > Privacy & Security > Permissions > Block pop-up windows (uncheck)',
      ],
    }
  }

  if (/edg/.test(userAgent)) {
    return {
      browser: 'Edge',
      steps: [
        'Click the lock icon next to the URL',
        'Next to "Pop-ups and redirects", select "Allow"',
        'Reload the page',
        'Alternatively: Settings > Privacy, search, and services > Site permissions > Pop-ups and redirects',
      ],
    }
  }

  // Fallback for unknown browsers
  return {
    browser: 'Your Browser',
    steps: [
      'Look for a notification or icon indicating blocked popups',
      'Check browser settings under Privacy/Security/Permissions',
      'Search for "pop-up" in settings and enable it for this site',
    ],
  }
}
